/* ------------------------------------------------------------------
   XSREZ - make a FLICK advance one slide on touch.

   Companion to assets/xs-snap.css. That file keeps scroll snapping
   switched on for coarse pointers; this file supplies the gesture
   Horizon never wrote.

   THE GAP. assets/slideshow.js binds its drag handler to `mousedown`
   only - verified on the live bundle: two `mousedown` matches, one
   `pointerdown`, zero touchstart/touchmove/touchend. Its onPointerUp
   contains explicit flick-advances-one-slide logic, but that path is
   only ever reached by a mouse. On a phone a short, fast swipe moves
   the scroller a little way, the finger lifts, and mandatory snap
   correctly settles back to the slide it started on. Snapping is
   working; there is simply nothing translating "flick" into "next".

   WHAT THIS DOES. Watches touch gestures on any slideshow-slides,
   measures the velocity of the last few milliseconds, and if the
   gesture reads as a flick that did NOT travel far enough for the
   browser to settle on the next slide by itself, calls the
   component's own next() / previous().

   Using the component's public API rather than scrolling by hand is
   deliberate: next() also updates `current`, the dot indicators, the
   slide counter and the aria-hidden bookkeeping. A raw scrollTo
   would move the pictures and leave all of that stale.

   WHY IT IS SAFE AGAINST A THEME UPDATE. Nothing here patches a
   Horizon file. It is an additive listener on document, and the only
   Horizon surface it touches is the documented custom-element API on
   slideshow-component. If Shopify ever ships real touch support, the
   half-slide guard below means this stops firing on its own rather
   than double-advancing.

   TUNING. MIN_V is the honest flick threshold. SOFT_V plus SOFT_DX
   catches the slower, longer drag that still clearly meant "next".
   Raise MIN_V if the carousel feels twitchy; raise SOFT_DX if a
   near-stationary tap-and-wobble ever advances a slide.
   ------------------------------------------------------------------ */

(function () {
  'use strict';

  if (!window.matchMedia || !window.matchMedia('(pointer: coarse)').matches) return;

  var MIN_V = 0.3;    // px per ms - a decisive flick
  var SOFT_V = 0.15;  // px per ms - a slower drag, but see SOFT_DX
  var SOFT_DX = 15;   // px - how far that slower drag must travel
  var SAMPLE = 120;   // ms - ignore velocity measured over a longer gap than this

  var state = new WeakMap();

  function scrollerFor(target) {
    if (!target || typeof target.closest !== 'function') return null;
    return target.closest('slideshow-slides');
  }

  function onStart(e) {
    if (e.touches && e.touches.length > 1) return;
    var el = scrollerFor(e.target);
    if (!el) return;
    var t = e.touches ? e.touches[0] : e;
    state.set(el, {
      x0: t.clientX, y0: t.clientY,
      x: t.clientX, y: t.clientY, t: e.timeStamp,
      px: t.clientX, pt: e.timeStamp,
      multi: false
    });
  }

  function onMove(e) {
    var el = scrollerFor(e.target);
    if (!el) return;
    var s = state.get(el);
    if (!s) return;
    if (e.touches && e.touches.length > 1) { s.multi = true; return; }
    var t = e.touches ? e.touches[0] : e;
    // Keep one older sample so the release velocity is measured over a
    // real interval rather than two events that arrived in the same ms.
    if (e.timeStamp - s.t > 8) { s.px = s.x; s.pt = s.t; }
    s.x = t.clientX;
    s.y = t.clientY;
    s.t = e.timeStamp;
  }

  function onEnd(e) {
    var el = scrollerFor(e.target);
    if (!el) return;
    var s = state.get(el);
    state.delete(el);
    if (!s || s.multi) return;

    var dx = s.x - s.x0;
    var dy = s.y - s.y0;

    // Vertical intent - the shopper is scrolling the page, not the carousel.
    if (Math.abs(dx) <= Math.abs(dy) * 1.2) return;

    var dt = s.t - s.pt;
    var v = (dt > 0 && dt < SAMPLE) ? (s.x - s.px) / dt : 0;
    var av = Math.abs(v);

    var isFlick = av >= MIN_V || (av >= SOFT_V && Math.abs(dx) >= SOFT_DX);
    if (!isFlick) return;

    var first = el.firstElementChild;
    if (!first || !first.offsetWidth) return;

    // Past half a slide the browser already settles on the next one.
    // Leaving that case alone is what stops this double-advancing.
    if (Math.abs(dx) >= first.offsetWidth * 0.5) return;

    var comp = el.closest('slideshow-component');
    if (!comp || typeof comp.next !== 'function' || typeof comp.previous !== 'function') return;
    if (comp.disabled === true || comp.getAttribute('disabled') === 'true') return;
    if (comp.slides && comp.slides.length < 2) return;

    if (dx < 0) comp.next(); else comp.previous();
  }

  function onCancel(e) {
    var el = scrollerFor(e.target);
    if (el) state.delete(el);
  }

  // Passive and capturing. Passive because nothing here ever calls
  // preventDefault, so the browser is free to keep scrolling at 60fps.
  // Capturing so the gesture is seen even if something inside a slide
  // stops propagation. Delegated to document so carousels added later
  // by the section rendering API are covered without a MutationObserver.
  var opts = { passive: true, capture: true };
  document.addEventListener('touchstart', onStart, opts);
  document.addEventListener('touchmove', onMove, opts);
  document.addEventListener('touchend', onEnd, opts);
  document.addEventListener('touchcancel', onCancel, opts);
})();